import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import { blogPostsData } from "../data/blogPosts";

export default function BlogDetail() {
  const { slug } = useParams();
  const post = blogPostsData[slug];

  if (!post) {
    return (
      <main className="pt-28 pb-16 px-4 text-center">
        <h1 className="text-4xl text-white font-['Playfair_Display']">Blog Post Not Found</h1>
        <Link to="/" className="text-purple-400 mt-4 inline-block">Go Home</Link>
      </main>
    );
  }

  return (
    <main data-testid="blog-detail-page" className="pt-28 pb-16">
      {/* Hero */}
      <section className="px-4">
        <div className="max-w-4xl mx-auto">
          {/* Back link */}
          <Link to="/" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-8 transition-colors" data-testid="blog-back-link">
            <ArrowLeft size={16} /> Back to Home
          </Link>

          {/* Meta */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center gap-4 mb-6">
              <span className="bg-purple-600 text-white text-xs px-4 py-1.5 rounded-full flex items-center gap-1">
                <Tag size={12} /> {post.category}
              </span>
              <span className="text-gray-400 text-sm flex items-center gap-1">
                <Calendar size={14} /> {post.date}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] text-white leading-tight mb-8">
              {post.title}
            </h1>
          </motion.div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-2xl overflow-hidden mb-12"
          >
            <img src={post.image} alt={post.title} className="w-full h-[300px] sm:h-[400px] object-cover" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {post.content.map((block, i) => {
              if (block.type === "heading") {
                return (
                  <h2 key={i} className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              return (
                <p key={i} className="text-gray-400 text-base leading-relaxed">
                  {block.text}
                </p>
              );
            })}
          </motion.div>

          {/* CTA */}
          <div className="mt-16 bg-gradient-to-br from-purple-900/20 to-transparent border border-purple-900/20 rounded-2xl p-6 sm:p-8 text-center">
            <h3 className="text-white font-semibold text-xl mb-3">Ready to start your project?</h3>
            <p className="text-gray-400 text-sm mb-6">Let's discuss how Digitronix Consultancy can help transform your business.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 cta-button text-white px-6 py-3 rounded-full text-sm font-semibold" data-testid="blog-cta">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
